import React, { useEffect, useRef } from 'react';
import { Quote } from 'lucide-react';
import { testimonials } from '../data/mockData';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const TestimonialsSection = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const cardsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Title animation
      gsap.fromTo(titleRef.current,
        { x: -80, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 75%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Cards stagger animation
      const cards = cardsRef.current?.querySelectorAll('.testimonial-card');
      if (cards) {
        gsap.fromTo(cards,
          { y: 100, opacity: 0, rotationY: 15 },
          {
            y: 0,
            opacity: 1,
            rotationY: 0,
            duration: 0.8,
            stagger: 0.2,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: cardsRef.current,
              start: 'top 80%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="py-20 bg-gray-100 relative overflow-hidden" ref={sectionRef}>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div ref={titleRef} className="mb-16">
          <span className="text-[#00A86B] font-bold text-sm tracking-wider">TESTIMONIALS</span>
          <h2 className="text-4xl md:text-5xl font-black text-[#0A1628] mt-4 mb-2">
            REAL CONTRACTORS.
          </h2>
          <h2 className="text-4xl md:text-5xl font-black text-[#00A86B] mb-6">
            REAL RESULTS.
          </h2>
        </div>

        {/* Testimonial Cards */}
        <div ref={cardsRef} className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index} 
              className="testimonial-card bg-white p-8 rounded-lg border-b-4 border-[#0A1628] cursor-pointer transform-gpu transition-all duration-300 hover:-translate-y-2"
              style={{
                boxShadow: '0 10px 30px rgba(0,0,0,0.15), 0 5px 15px rgba(0,0,0,0.1)',
              }}
            >
              {/* Quote icon */}
              <div className="mb-6">
                <Quote className="w-12 h-12 text-[#00A86B]/30" />
              </div>
              
              {/* Quote text */}
              <p className="text-[#0A1628] text-lg mb-8 leading-relaxed">
                "{testimonial.quote}"
              </p>
              
              {/* Divider */}
              <div className="w-full h-px bg-gray-200 mb-6"></div>
              
              {/* Author info */}
              <div>
                <h4 className="font-bold text-[#0A1628] text-lg">{testimonial.name}</h4>
                <p className="text-gray-500 text-sm mb-4">{testimonial.company}</p>
                <div className="inline-block bg-[#00A86B]/10 px-4 py-2 rounded-full">
                  <span className="text-[#00A86B] font-bold text-sm">FUNDED: {testimonial.funded}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
